import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getLibraryAssetBySlug, getMyLibrary } from "../../services/libraryApi";
import { createLibraryCheckoutSession, createLibraryNftMintCheckoutSession } from "../../services/libraryPurchaseApi";
import { trackProductViewed, trackCheckoutStarted } from "../../services/analyticsClient";
import { useAuth } from "../../context/AuthContext";
import AuthModal from "../../components/auth/AuthModal";
import { libraryAsset } from "../../utils/libraryAssets";
import {
  GENESIS_COMIC_PRODUCT_JSON_LD,
  KORAXOS_BOOK_JSON_LD,
  buildCanonical,
  DEFAULT_OG_IMAGE_LIBRARY,
} from "../../constants/siteSeo";
import { setPageMeta, clearPageMeta } from "../../utils/setPageMeta";
import {
  setPendingLibraryPurchase,
  clearPendingLibraryPurchase,
  getPendingLibraryPurchase,
} from "../../utils/pendingLibraryPurchase";
import { clampNftCheckoutQuantity, effectiveMaxNftCheckoutForTier, maxNftCheckoutForTier } from "../../utils/libraryNftPurchaseCaps";
import { COLLECTOR_EDITION_PURCHASE_BLURB } from "../../data/libraryMockData";
import LibraryNftWalletPurchaseControls from "../../components/library/LibraryNftWalletPurchaseControls";
import LibraryNftV2DirectMintControls from "../../components/library/LibraryNftV2DirectMintControls";
import LibraryPurchaseLegalModal from "../../components/legal/LibraryPurchaseLegalModal";
import "./library.css";

const FALLBACK_IMG = libraryAsset("00_library_page_master_mockup.webp");

/** Cinematic hero: prefer dedicated hero art, then cover, then brand fallback. */
function resolveHeroBackground(asset) {
  if (!asset) return FALLBACK_IMG;
  const h = (asset.heroImage && String(asset.heroImage).trim()) || "";
  const c = (asset.coverImage && String(asset.coverImage).trim()) || "";
  if (h) return h;
  if (c) return c;
  return FALLBACK_IMG;
}

/** Product identity on cards and showcase: cover first, then hero, then fallback. */
function resolveCoverArt(asset) {
  if (!asset) return FALLBACK_IMG;
  const c = (asset.coverImage && String(asset.coverImage).trim()) || "";
  const h = (asset.heroImage && String(asset.heroImage).trim()) || "";
  if (c) return c;
  if (h) return h;
  return FALLBACK_IMG;
}

function formatUsd(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(x);
}

export default function LibraryAssetDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [purchasingEditionId, setPurchasingEditionId] = useState("");
  const [alreadyOwnedInfo, setAlreadyOwnedInfo] = useState(null);
  const [ownedNftEditionIds, setOwnedNftEditionIds] = useState(() => new Set());
  const [ownedDigitalEditionIds, setOwnedDigitalEditionIds] = useState(() => new Set());
  const [detailCoverBroken, setDetailCoverBroken] = useState(false);
  const [nftQtyByEdition, setNftQtyByEdition] = useState({});
  const prevAuthRef = useRef(null);
  const [legalAck, setLegalAck] = useState({
    libraryDigitalOk: false,
    libraryNftOk: false,
    vrPurchaseOk: false,
  });
  const [libraryLegalModalOpen, setLibraryLegalModalOpen] = useState(false);
  const [libraryLegalModalKind, setLibraryLegalModalKind] = useState("digital");
  const libraryLegalPendingRef = useRef(null);
  const walletEthResumeByEditionRef = useRef({});
  const walletV2ResumeByEditionRef = useRef({});

  const openLibraryLegalModal = (kind, pending) => {
    libraryLegalPendingRef.current = pending;
    setLibraryLegalModalKind(kind);
    setLibraryLegalModalOpen(true);
  };

  const openWalletNftLegal = (editionId, resumeRail) => {
    clearPendingLibraryPurchase();
    setCheckoutError("");
    openLibraryLegalModal("nft", {
      editionId: String(editionId || "").trim(),
      resumeRail,
    });
  };

  const slugNorm = useMemo(() => String(slug || "").trim().toLowerCase(), [slug]);

  const startDigitalCheckout = async (editionId) => {
    const eid = String(editionId || "").trim();
    if (!eid) return;
    setAlreadyOwnedInfo(null);
    if (!isAuthenticated) {
      setPendingLibraryPurchase({
        editionId: eid,
        assetSlug: slugNorm,
        assetId: payload?.asset?._id != null ? String(payload.asset._id) : "",
        purchaseKind: "digital",
        returnPath: `${window.location.pathname}${window.location.search}`,
        authGate: "email",
      });
      setAuthOpen(true);
      return;
    }
    if (!legalAck.libraryDigitalOk) {
      openLibraryLegalModal("digital", { editionId: eid, ctx: {}, resumeRail: "stripe_digital" });
      return;
    }
    setCheckoutError("");
    setPurchasingEditionId(eid);
    trackCheckoutStarted({
      productType: "library_digital",
      editionId: eid,
      assetSlug: slugNorm,
    });
    try {
      const result = await createLibraryCheckoutSession(eid);
      clearPendingLibraryPurchase();
      if (result?.alreadyOwned) {
        setAlreadyOwnedInfo(result);
        return;
      }
      if (result?.url) window.location.assign(result.url);
    } catch (err) {
      setCheckoutError(err?.message || "Checkout failed.");
    } finally {
      setPurchasingEditionId("");
    }
  };

  const startNftMintCheckout = async (editionId, ctx = {}) => {
    const eid = String(editionId || "").trim();
    if (!eid) return;
    setAlreadyOwnedInfo(null);
    const tierKey = String(ctx.tierKey || "").trim();
    const rawQty = ctx.quantity != null ? ctx.quantity : nftQtyByEdition[eid];
    const qty = clampNftCheckoutQuantity(tierKey, rawQty ?? 1, ctx.inventoryRemaining);
    if (!isAuthenticated) {
      setPendingLibraryPurchase({
        editionId: eid,
        assetSlug: slugNorm,
        assetId: payload?.asset?._id != null ? String(payload.asset._id) : "",
        purchaseKind: "nft_mint",
        returnPath: `${window.location.pathname}${window.location.search}`,
        quantity: qty,
        tierKey,
        authGate: "email",
      });
      setAuthOpen(true);
      return;
    }
    if (!legalAck.libraryNftOk) {
      openLibraryLegalModal("nft", { editionId: eid, ctx, resumeRail: "stripe_nft" });
      return;
    }
    setCheckoutError("");
    setPurchasingEditionId(eid);
    trackCheckoutStarted({
      productType: "library_nft_mint",
      editionId: eid,
      assetSlug: slugNorm,
      quantity: qty,
      tierKey,
    });
    try {
      const result = await createLibraryNftMintCheckoutSession(eid, qty);
      clearPendingLibraryPurchase();
      if (result?.alreadyOwned) {
        setAlreadyOwnedInfo(result);
        return;
      }
      if (result?.url) window.location.assign(result.url);
    } catch (err) {
      setCheckoutError(err?.message || "Checkout could not be started.");
    } finally {
      setPurchasingEditionId("");
    }
  };

  const handleLibraryLegalModalContinue = () => {
    const kind = libraryLegalModalKind;
    const pending = libraryLegalPendingRef.current;
    const ok = kind === "nft" ? legalAck.libraryNftOk : legalAck.libraryDigitalOk;
    if (!ok) return;
    setLibraryLegalModalOpen(false);
    libraryLegalPendingRef.current = null;

    if (pending?.resumeRail === "wallet_eth") {
      walletEthResumeByEditionRef.current[pending.editionId]?.();
      return;
    }
    if (pending?.resumeRail === "wallet_v2") {
      walletV2ResumeByEditionRef.current[pending.editionId]?.();
      return;
    }
    if (!pending?.editionId) return;
    if (kind === "nft") void startNftMintCheckout(pending.editionId, pending.ctx || {});
    else void startDigitalCheckout(pending.editionId);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      prevAuthRef.current = false;
      return;
    }
    const wasLoggedOut = prevAuthRef.current === false;
    prevAuthRef.current = true;
    if (!wasLoggedOut) return;

    const pending = getPendingLibraryPurchase();
    if (!pending?.editionId) return;
    if (pending.authGate !== "email") {
      clearPendingLibraryPurchase();
      return;
    }

    const needsLegal =
      pending.purchaseKind === "nft_mint" ? legalAck.libraryNftOk : legalAck.libraryDigitalOk;
    if (!needsLegal) {
      setCheckoutError("");
      openLibraryLegalModal(pending.purchaseKind === "nft_mint" ? "nft" : "digital", {
        editionId: String(pending.editionId),
        resumeRail: pending.purchaseKind === "nft_mint" ? "stripe_nft" : "stripe_digital",
        ctx: {
          assetSlug: pending.assetSlug || "",
          assetId: pending.assetId || "",
          quantity: pending.quantity,
          tierKey: pending.tierKey || "",
        },
      });
      setPurchasingEditionId("");
      return;
    }

    let cancelled = false;
    (async () => {
      setCheckoutError("");
      setPurchasingEditionId(String(pending.editionId));
      try {
        const isNft = pending.purchaseKind === "nft_mint";
        const pendingQty = clampNftCheckoutQuantity(
          pending.tierKey || "",
          pending.quantity != null ? pending.quantity : 1
        );
        const result = isNft
          ? await createLibraryNftMintCheckoutSession(String(pending.editionId), pendingQty)
          : await createLibraryCheckoutSession(String(pending.editionId));
        if (cancelled) return;
        clearPendingLibraryPurchase();
        setAuthOpen(false);
        if (result?.alreadyOwned) {
          setAlreadyOwnedInfo(result);
          return;
        }
        if (result?.url) window.location.assign(result.url);
      } catch (e) {
        if (!cancelled) setCheckoutError(e?.message || "Checkout could not be started.");
      } finally {
        if (!cancelled) setPurchasingEditionId("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, legalAck.libraryDigitalOk, legalAck.libraryNftOk]);

  useEffect(() => {
    if (!isAuthenticated) {
      setOwnedNftEditionIds(new Set());
      setOwnedDigitalEditionIds(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const d = await getMyLibrary();
        const nftSet = new Set();
        const digitalSet = new Set();
        (Array.isArray(d?.items) ? d.items : []).forEach((row) => {
          const id = row?.edition?._id != null ? String(row.edition._id) : "";
          if (!id) return;
          if (row?.edition?.editionType === "nft") nftSet.add(id);
          if (row?.edition?.editionType === "digital") digitalSet.add(id);
        });
        if (!cancelled) {
          setOwnedNftEditionIds(nftSet);
          setOwnedDigitalEditionIds(digitalSet);
        }
      } catch {
        if (!cancelled) {
          setOwnedNftEditionIds(new Set());
          setOwnedDigitalEditionIds(new Set());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await getLibraryAssetBySlug(slug);
        if (!cancelled) setPayload(data);
      } catch {
        if (!cancelled) {
          setError(true);
          setPayload(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    const assetId = payload?.asset?._id != null ? String(payload.asset._id) : slugNorm;
    if (!assetId || !payload?.asset) return;
    trackProductViewed("library_asset", assetId, {
      slug: slugNorm,
      title: payload.asset.title || undefined,
    });
  }, [payload?.asset?._id, payload?.asset?.title, slugNorm]);

  useEffect(() => {
    setDetailCoverBroken(false);
  }, [slug]);

  const asset = payload?.asset;
  const editions = useMemo(() => (Array.isArray(payload?.editions) ? payload.editions : []), [payload]);

  const showMerchLegalCheckbox = useMemo(() => {
    return editions.some((ed) => {
      if (ed.editionType !== "nft") return false;
      const eid = ed._id != null ? String(ed._id) : "";
      const remRaw = ed?.nftInventoryRemaining;
      const remNum =
        remRaw != null && Number.isFinite(Number(remRaw)) ? Math.max(0, Math.floor(Number(remRaw))) : null;
      const invExhausted = remNum === 0;
      const priceOk = Number.isFinite(Number(ed.priceUsd)) && Number(ed.priceUsd) > 0;
      const saleOn = ed.saleActive !== false;
      return Boolean(eid && priceOk && saleOn && !invExhausted);
    });
  }, [editions]);

  const heroBg = useMemo(() => resolveHeroBackground(asset), [asset]);
  const coverArt = useMemo(() => resolveCoverArt(asset), [asset]);
  const showcaseCoverSrc = detailCoverBroken ? FALLBACK_IMG : coverArt;
  const displayHeadline = String(asset?.marketingHeadline || asset?.title || "").trim();
  const displayTagline = String(
    asset?.marketingSubheadline || asset?.shortDescription || asset?.type || ""
  ).trim();
  const displayHook = String(asset?.marketingHook || "").trim();
  const marketingBullets = Array.isArray(asset?.marketingBullets) ? asset.marketingBullets.filter(Boolean) : [];
  const isBook = asset?.type === "book";

  useEffect(() => {
    if (!asset) return;
    const title = `${asset.title} | Treydora Library`;
    const desc =
      String(asset.marketingSubheadline || asset.shortDescription || asset.description || "").slice(0, 320) ||
      "Treydora Library";
    const slugPath = asset.slug ? `/library/${asset.slug}` : "/library";
    const jsonLd =
      asset.type === "book"
        ? { ...KORAXOS_BOOK_JSON_LD, name: asset.title || KORAXOS_BOOK_JSON_LD.name, description: desc }
        : asset.type === "comic" || asset.type === "nft_comic"
          ? {
              ...GENESIS_COMIC_PRODUCT_JSON_LD,
              name: asset.title || GENESIS_COMIC_PRODUCT_JSON_LD.name,
              description: desc,
            }
          : undefined;
    setPageMeta({
      title,
      description: desc,
      ogTitle: title,
      ogDescription: String(asset.marketingSubheadline || asset.shortDescription || "").slice(0, 200),
      ogImage: DEFAULT_OG_IMAGE_LIBRARY,
      ogType: "website",
      canonicalUrl: buildCanonical(slugPath),
      ogUrl: buildCanonical(slugPath),
      jsonLd,
    });
    return () => clearPageMeta();
  }, [asset]);

  if (loading) {
    return (
      <div className="lib-storefront">
        <div className="lib-inner" style={{ paddingTop: 120, paddingBottom: 120 }}>
          <p className="lib-sec-desc" style={{ textAlign: "center" }}>
            Opening the Treydora Library...
          </p>
        </div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="lib-storefront">
        <div className="lib-inner" style={{ paddingTop: 120, paddingBottom: 120 }}>
          <p className="lib-sec-desc" style={{ textAlign: "center" }}>
            The Library could not be reached.
          </p>
          <p style={{ textAlign: "center", marginTop: 16 }}>
            <Link to="/library" className="lib-hero__vault-link">
              Back to Library
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lib-storefront">
      <section
        className="lib-hero lib-hero--storefront lib-hero--detail"
        aria-labelledby="lib-detail-hero-title"
        style={{ "--lib-hero-img": `url(${heroBg})` }}
      >
        <div className="lib-hero__bg" aria-hidden />
        <div className="lib-hero__vignette" aria-hidden />
        <div className="lib-hero__grid" aria-hidden />
        <div className="lib-hero__inner">
          <div className="lib-hero__label lib-hud">Treydora Archive</div>
          <h1 id="lib-detail-hero-title" className="lib-hero__title lib-serif">
            {displayHeadline}
          </h1>
          {displayTagline ? <p className="lib-hero__subtitle">{displayTagline}</p> : null}
          {displayHook ? (
            <p className="lib-hero__lede" style={{ color: "rgba(244, 228, 168, 0.92)", maxWidth: 640 }}>
              {displayHook}
            </p>
          ) : null}
          <p className="lib-hero__vault">
            <Link to="/library" className="lib-hero__vault-link">
              ← Back to storefront
            </Link>
          </p>
        </div>
      </section>

      <div className="lib-rule" aria-hidden />

      <div className="lib-inner lib-inner--detail">
        {checkoutError ? (
          <p className="lib-sec-desc" role="alert" style={{ color: "#ffb4a8", marginBottom: 16 }}>
            {checkoutError}
          </p>
        ) : null}
        {alreadyOwnedInfo?.alreadyOwned ? (
          <div
            className="lib-sec-desc"
            role="status"
            style={{
              marginBottom: 20,
              padding: "14px 18px",
              border: "1px solid rgba(110, 231, 183, 0.35)",
              borderRadius: 8,
              background: "rgba(6, 40, 32, 0.45)",
            }}
          >
            <p style={{ margin: "0 0 10px", fontWeight: 600 }}>You already own this edition.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              <Link to="/dashboard/library" className="lib-btn lib-btn--gold lib-btn--sm" style={{ textDecoration: "none" }}>
                Open My Library
              </Link>
              {alreadyOwnedInfo.readUrl && String(alreadyOwnedInfo.readUrl).startsWith("/") ? (
                <Link to={alreadyOwnedInfo.readUrl} className="lib-btn lib-btn--primary lib-btn--sm" style={{ textDecoration: "none" }}>
                  Open reader
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}

        <section className="lib-detail-showcase" aria-labelledby="lib-detail-showcase-heading">
          <div className="lib-detail-showcase__visual" style={{ position: "relative" }}>
            <span id="lib-detail-showcase-heading" className="lib-sec-eyebrow lib-detail-showcase__eyebrow">
              Product art
            </span>
            <div className="lib-digital-card__frame lib-detail-showcase__frame" aria-hidden>
              <div className="lib-digital-card__frame-glow" />
              <img
                className="lib-digital-card__cover-img"
                src={showcaseCoverSrc}
                alt={`${asset.title} cover`}
                loading="eager"
                decoding="async"
                onError={() => setDetailCoverBroken(true)}
              />
            </div>
            {detailCoverBroken ? <p className="lib-digital-card__cover-fallback-note">Cover art unavailable</p> : null}
          </div>
          <div className="lib-detail-showcase__copy">
            <h2 className="lib-sec-title lib-serif" style={{ marginBottom: 14 }}>
              About this release
            </h2>
            <div className="lib-sec-desc lib-detail-showcase__body">
              {asset.description || asset.shortDescription || ""}
            </div>
            {marketingBullets.length ? (
              <>
                <h3 className="lib-detail-bullets__title">Why readers choose this release</h3>
                <ul className="lib-detail-bullets">
                  {marketingBullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </section>

        {editions.length ? (
          <section className="lib-sec lib-sec--digital" aria-labelledby="lib-detail-editions">
            <header className="lib-sec-head lib-sec-head--digital-store">
              <span className="lib-sec-eyebrow lib-sec-eyebrow--blue">Editions</span>
              <h2 id="lib-detail-editions" className="lib-sec-title">
                Secure your digital copy
              </h2>
              <p className="lib-sec-desc" style={{ maxWidth: 640 }}>
                Instant vault access after checkout. Reader opens in your Treydora account.
              </p>
            </header>
            <div className="lib-digital-grid">
              {editions.map((e) => {
                const editionId = e._id != null ? String(e._id) : "";
                const isNft = e.editionType === "nft";
                const tierKey = String(e.tierKey || "").trim();
                const remRaw = e?.nftInventoryRemaining;
                const remNum =
                  isNft && remRaw != null && Number.isFinite(Number(remRaw))
                    ? Math.max(0, Math.floor(Number(remRaw)))
                    : null;
                const invExhausted = isNft && remNum === 0;
                const priceOk = Number.isFinite(Number(e.priceUsd)) && Number(e.priceUsd) > 0;
                const saleOn = e.saleActive !== false;
                const ownedNft = isNft && editionId && ownedNftEditionIds.has(editionId);
                const ownedDigital = !isNft && Boolean(editionId && ownedDigitalEditionIds.has(editionId));
                const displayedQty = isNft
                  ? clampNftCheckoutQuantity(tierKey, nftQtyByEdition[editionId] ?? 1, remNum)
                  : 1;
                const maxCheckout = isNft ? effectiveMaxNftCheckoutForTier(tierKey, remNum) : maxNftCheckoutForTier(tierKey);

                let nftGoldLabel = "Edition unavailable";
                let nftGoldDisabled = true;
                let nftGoldOnClick = null;
                if (isNft) {
                  if (editionId && !saleOn) {
                    nftGoldLabel = "Not on sale";
                    nftGoldDisabled = true;
                  } else if (editionId && priceOk && saleOn && invExhausted) {
                    nftGoldLabel = "Sold out";
                    nftGoldDisabled = true;
                    nftGoldOnClick = null;
                  } else if (editionId && priceOk && saleOn) {
                    nftGoldLabel =
                      purchasingEditionId === editionId
                        ? "Preparing checkout…"
                        : ownedNft
                          ? displayedQty > 1
                            ? "Buy More Editions"
                            : "Buy Another Edition"
                          : "Buy with Card";
                    nftGoldDisabled = Boolean(purchasingEditionId);
                    nftGoldOnClick = () =>
                      void startNftMintCheckout(editionId, {
                        tierKey,
                        quantity: displayedQty,
                        inventoryRemaining: remNum,
                      });
                  } else if (editionId && !priceOk) {
                    nftGoldLabel = "Edition unavailable";
                    nftGoldDisabled = true;
                  }
                }

                const editionDesc = isNft
                  ? COLLECTOR_EDITION_PURCHASE_BLURB
                  : String(e.marketingCopy || "").trim() ||
                    e.accessSummary ||
                    e.rightsSummary ||
                    (Array.isArray(e.benefits) && e.benefits.length ? e.benefits.join(" · ") : "");

                const isPurchasingDigital = !isNft && purchasingEditionId === editionId;
                let digitalCtaLabel = e.tierKey === "digital_book" ? "Buy Digital Book" : "Buy Digital Copy";
                let digitalCtaDisabled = !editionId || isPurchasingDigital;
                let digitalCtaBusy = isPurchasingDigital;
                if (!isNft) {
                  if (!editionId) {
                    digitalCtaLabel = "Unavailable — inventory not ready";
                    digitalCtaDisabled = true;
                    digitalCtaBusy = false;
                  } else if (!ownedDigital && !saleOn) {
                    digitalCtaLabel = "Not on sale";
                    digitalCtaDisabled = true;
                    digitalCtaBusy = false;
                  } else if (!ownedDigital && !priceOk) {
                    digitalCtaLabel = "Edition not on sale yet";
                    digitalCtaDisabled = true;
                    digitalCtaBusy = false;
                  } else if (!ownedDigital && isPurchasingDigital) {
                    digitalCtaLabel = "Preparing checkout…";
                    digitalCtaDisabled = true;
                    digitalCtaBusy = true;
                  }
                }

                return (
                  <article key={e._id || e.slug} className="lib-digital-card">
                    <div className="lib-digital-card__media">
                      <div className="lib-digital-card__frame" aria-hidden>
                        <div className="lib-digital-card__frame-glow" />
                        <img
                          className="lib-digital-card__cover-img"
                          src={coverArt}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                    </div>
                    <div className="lib-digital-card__body">
                      <ul className="lib-digital-card__badges" aria-label="Edition tags">
                        <li
                          className={`lib-digital-card__badge ${e.editionType === "nft" ? "lib-digital-card__badge--muted" : "lib-digital-card__badge--accent"}`}
                        >
                          {e.editionType === "nft" ? "Collector" : "Digital"}
                        </li>
                        <li className="lib-digital-card__badge">{isBook ? "Book" : "Comic"}</li>
                        {e.tierKey ? (
                          <li className="lib-digital-card__badge lib-digital-card__badge--muted">{e.tierKey}</li>
                        ) : null}
                      </ul>
                      <h3 className="lib-digital-card__title lib-serif">{e.title}</h3>
                      <p className="lib-digital-card__desc">{editionDesc}</p>
                      <div className="lib-digital-card__commerce">
                        <div className="lib-digital-card__price-block">
                          {e.editionType === "nft" ? (
                            <>
                              <span className="lib-digital-card__price-label">Per edition</span>
                              <p className="lib-digital-card__price">{formatUsd(e.priceUsd)} each</p>
                              {remNum != null ? (
                                <p className="lib-sec-desc" style={{ margin: "6px 0 0", fontSize: "0.76rem", opacity: 0.85 }}>
                                  <strong>{remNum.toLocaleString()} remain</strong>
                                  {e?.nftInventoryTierMaxSlots != null &&
                                  Number.isFinite(Number(e.nftInventoryTierMaxSlots))
                                    ? ` · tier capacity ${Number(e.nftInventoryTierMaxSlots).toLocaleString()}`
                                    : ""}
                                </p>
                              ) : null}
                              {editionId && priceOk && saleOn && !invExhausted ? (
                                <div className="lib-nft-qty lib-nft-qty--compact" aria-label="Purchase quantity">
                                  <span className="lib-nft-qty__label">Quantity</span>
                                  <div className="lib-nft-qty__btns">
                                    <button
                                      type="button"
                                      className="lib-nft-qty__step"
                                      aria-label="Decrease quantity"
                                      disabled={nftGoldDisabled || displayedQty <= 1}
                                      onClick={() =>
                                        setNftQtyByEdition((prev) => ({
                                          ...prev,
                                          [editionId]: clampNftCheckoutQuantity(
                                            tierKey,
                                            (prev[editionId] ?? 1) - 1,
                                            remNum
                                          ),
                                        }))
                                      }
                                    >
                                      −
                                    </button>
                                    <span className="lib-nft-qty__value" aria-live="polite">
                                      {displayedQty}
                                    </span>
                                    <button
                                      type="button"
                                      className="lib-nft-qty__step"
                                      aria-label="Increase quantity"
                                      disabled={nftGoldDisabled || displayedQty >= maxCheckout}
                                      onClick={() =>
                                        setNftQtyByEdition((prev) => ({
                                          ...prev,
                                          [editionId]: clampNftCheckoutQuantity(
                                            tierKey,
                                            (prev[editionId] ?? 1) + 1,
                                            remNum
                                          ),
                                        }))
                                      }
                                    >
                                      +
                                    </button>
                                  </div>
                                  <span className="lib-nft-qty__total">
                                    Total: {formatUsd(Number(e.priceUsd) * displayedQty)}
                                  </span>
                                  <span className="lib-nft-qty__cap">Max {maxCheckout} per checkout</span>
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <span className="lib-digital-card__price-label">Your price</span>
                              <p className="lib-digital-card__price">{formatUsd(e.priceUsd)}</p>
                            </>
                          )}
                        </div>
                        <div className="lib-digital-card__actions">
                          {e.editionType === "nft" ? (
                            <>
                              {ownedNft ? (
                                <button
                                  type="button"
                                  className="lib-btn lib-btn--sm"
                                  style={{ border: "1px solid rgba(200,155,79,0.45)", background: "transparent" }}
                                  onClick={() => navigate("/dashboard/library")}
                                >
                                  My Library
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="lib-btn lib-btn--gold lib-btn--sm"
                                disabled={nftGoldDisabled}
                                aria-busy={isNft && purchasingEditionId === editionId ? "true" : undefined}
                                onClick={nftGoldOnClick || undefined}
                              >
                                {nftGoldLabel}
                              </button>
                              {editionId && priceOk && saleOn && !invExhausted ? (
                                <LibraryNftWalletPurchaseControls
                                  editionId={editionId}
                                  quantity={displayedQty}
                                  disabled={Boolean(purchasingEditionId)}
                                  purchaseAllowed={legalAck.libraryNftOk}
                                  onLegalAcknowledgmentRequired={() => openWalletNftLegal(editionId, "wallet_eth")}
                                  registerWalletResume={(fn) => {
                                    if (fn) walletEthResumeByEditionRef.current[editionId] = fn;
                                    else delete walletEthResumeByEditionRef.current[editionId];
                                  }}
                                  compact
                                />
                              ) : null}
                              {editionId && priceOk && saleOn && !invExhausted ? (
                                <LibraryNftV2DirectMintControls
                                  disabled={Boolean(purchasingEditionId)}
                                  purchaseAllowed={legalAck.libraryNftOk}
                                  onLegalAcknowledgmentRequired={() => openWalletNftLegal(editionId, "wallet_v2")}
                                  registerWalletResume={(fn) => {
                                    if (fn) walletV2ResumeByEditionRef.current[editionId] = fn;
                                    else delete walletV2ResumeByEditionRef.current[editionId];
                                  }}
                                  compact
                                />
                              ) : null}
                            </>
                          ) : (
                            <>
                              {ownedDigital ? (
                                <Link
                                  to="/dashboard/library"
                                  className="lib-btn lib-btn--digital-owned"
                                  style={{ textDecoration: "none" }}
                                >
                                  Owned — open in library
                                </Link>
                              ) : (
                                <button
                                  type="button"
                                  className="lib-btn lib-btn--digital-buy"
                                  disabled={digitalCtaDisabled}
                                  aria-busy={digitalCtaBusy ? "true" : undefined}
                                  onClick={() => startDigitalCheckout(editionId)}
                                >
                                  {digitalCtaLabel}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ) : null}

        <p className="lib-detail-backfoot">
          <Link to="/library" className="lib-hero__vault-link">
            ← Back to storefront
          </Link>
        </p>
      </div>
      <LibraryPurchaseLegalModal
        isOpen={libraryLegalModalOpen}
        onClose={() => {
          setLibraryLegalModalOpen(false);
          libraryLegalPendingRef.current = null;
        }}
        purchaseKind={libraryLegalModalKind}
        showMerchCheckbox={libraryLegalModalKind === "nft" && showMerchLegalCheckbox}
        legalAck={legalAck}
        onValidityChange={setLegalAck}
        onContinue={handleLibraryLegalModalContinue}
      />
      <AuthModal
        isOpen={authOpen}
        onClose={() => setAuthOpen(false)}
        initialMessage="Log in or create an account to continue checkout. Your purchase is tied to your Treydora library after payment."
        loginSuccessMessage="Signed in. Starting secure checkout…"
        registerSuccessMessage="Account ready. Starting secure checkout…"
      />
    </div>
  );
}
